import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab
} from '@material-ui/core';
import {
  TrendingUp,
  People,
  AttachMoney,
  Share,
  QrCode,
  GetApp,
  Refresh,
  CheckCircle,
  Schedule,
  Cancel,
  AccountBalance,
  Timeline,
  Assessment,
  Notifications,
  Settings,
  ExitToApp
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  statsCardGreen: {
    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  statsCardOrange: {
    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  statsCardPurple: {
    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  referralLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  qrCode: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  actionButton: {
    margin: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  referralCode: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  commissionInfo: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  }
}));

function AffiliateDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      idNumber: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Ghana'
      },
      gender: '',
      dateOfBirth: '',
      nationality: ''
    },
    mobileMoneyDetails: {
      provider: '',
      mobileNumber: '',
      accountName: ''
    }
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsTab, setSettingsTab] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Load profile data when settings dialog opens or when entering edit mode
  const loadProfileDataIntoForm = React.useCallback(() => {
    if (dashboardData?.affiliate) {
      // Load current data into form
      const personalInfo = dashboardData.affiliate.personalInfo || {};
      const bankDetails = dashboardData.affiliate.bankDetails || {};
      
      console.log('Loading profile data into form:', { 
        personalInfo, 
        bankDetails,
        fullDashboardData: dashboardData.affiliate 
      });
      
      const formData = {
        personalInfo: {
          fullName: personalInfo.fullName || '',
          email: personalInfo.email || '',
          phone: personalInfo.phone || '',
          idNumber: personalInfo.idNumber || '',
          address: {
            street: personalInfo.address?.street || '',
            city: personalInfo.address?.city || '',
            state: personalInfo.address?.state || '',
            zipCode: personalInfo.address?.zipCode || '',
            country: personalInfo.address?.country || 'Ghana'
          },
          gender: personalInfo.gender || '',
          dateOfBirth: personalInfo.dateOfBirth ? (personalInfo.dateOfBirth.split('T')[0] || '') : '',
          nationality: personalInfo.nationality || ''
        },
        mobileMoneyDetails: {
          provider: bankDetails.bankName || '',
          mobileNumber: bankDetails.accountNumber || '',
          accountName: bankDetails.accountName || ''
        }
      };
      
      console.log('Setting form data:', formData);
      setSettingsFormData(formData);
      return true; // Indicate success
    } else {
      console.warn('No dashboard data available to load into form', { dashboardData });
      return false; // Indicate failure
    }
  }, [dashboardData]);

  // Load profile data when settings dialog opens - Direct approach
  useEffect(() => {
    if (settingsDialogOpen && dashboardData?.affiliate) {
      // Always reset state when dialog opens
      setEditMode(false);
      setSettingsError('');
      setSettingsSuccess('');
      setSettingsTab(0);
      
      // Load data directly to avoid dependency issues
      const personalInfo = dashboardData.affiliate.personalInfo || {};
      const bankDetails = dashboardData.affiliate.bankDetails || {};
      
      console.log('Dialog opened, loading profile data directly:', { personalInfo, bankDetails });
      
      setSettingsFormData({
        personalInfo: {
          fullName: personalInfo.fullName || '',
          email: personalInfo.email || '',
          phone: personalInfo.phone || '',
          idNumber: personalInfo.idNumber || '',
          address: {
            street: personalInfo.address?.street || '',
            city: personalInfo.address?.city || '',
            state: personalInfo.address?.state || '',
            zipCode: personalInfo.address?.zipCode || '',
            country: personalInfo.address?.country || 'Ghana'
          },
          gender: personalInfo.gender || '',
          dateOfBirth: personalInfo.dateOfBirth ? (personalInfo.dateOfBirth.split('T')[0] || '') : '',
          nationality: personalInfo.nationality || ''
        },
        mobileMoneyDetails: {
          provider: bankDetails.bankName || '',
          mobileNumber: bankDetails.accountNumber || '',
          accountName: bankDetails.accountName || ''
        }
      });
    } else if (settingsDialogOpen && !dashboardData) {
      // If dashboard data is not loaded yet, fetch it
      console.log('Dialog opened but no dashboard data, fetching...');
      fetchDashboardData();
      // Reset state
      setEditMode(false);
      setSettingsError('');
      setSettingsSuccess('');
      setSettingsTab(0);
    }
  }, [settingsDialogOpen, dashboardData?.affiliate]); // Direct dependency on the data

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('affiliateToken');
      
      if (!token) {
        setError('Please login to access your dashboard');
        return;
      }

      // Use full backend URL or relative if proxied
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';
      const response = await fetch(`${API_BASE_URL}/api/affiliates/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch dashboard data' }));
        throw new Error(errorData.message || 'Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
      
      // Generate QR code for referral link
      if (data.affiliate.referralLink) {
        // Simple QR code generation - you can replace with a proper QR library
        setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.affiliate.referralLink)}`);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      // Check if error is due to HTML response (404 or server error page)
      if (err.message.includes('Unexpected token') || err.message.includes('<!DOCTYPE')) {
        setError('Unable to connect to server. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (dashboardData?.affiliate?.referralLink) {
      navigator.clipboard.writeText(dashboardData.affiliate.referralLink);
      // You could add a snackbar notification here
    }
  };

  const shareToWhatsApp = () => {
    if (dashboardData?.affiliate?.referralLink) {
      const text = encodeURIComponent(`Check out SIS Revolution School Management System! A comprehensive solution to digitize your school's activities. Join using my referral link: ${dashboardData.affiliate.referralLink}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }
  };

  const shareToFacebook = () => {
    if (dashboardData?.affiliate?.referralLink) {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(dashboardData.affiliate.referralLink)}`, '_blank');
    }
  };

  const shareToTwitter = () => {
    if (dashboardData?.affiliate?.referralLink) {
      const text = encodeURIComponent('Check out SIS Revolution School Management System! A comprehensive solution to digitize your school\'s activities.');
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(dashboardData.affiliate.referralLink)}`, '_blank');
    }
  };

  const downloadQRCode = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = 'affiliate-qr-code.png';
      link.href = qrCodeUrl;
      link.click();
    }
  };

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('affiliateToken');
    localStorage.removeItem('affiliateUser');
    
    // Redirect to login page
    history.push('/affiliate/login');
  };

  const handleSettingsChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    
    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      setSettingsFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: subChild ? {
            ...prev[parent][child],
            [subChild]: value
          } : value
        }
      }));
    } else {
      setSettingsFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSaveProfile = async () => {
    setSettingsLoading(true);
    setSettingsError('');
    setSettingsSuccess('');

    try {
      // Ensure phone number is in E.164 format
      let phoneNumber = settingsFormData.personalInfo.phone.trim();
      if (!phoneNumber.startsWith('+')) {
        if (phoneNumber.startsWith('0')) {
          phoneNumber = '+233' + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith('233')) {
          phoneNumber = '+' + phoneNumber;
        } else {
          phoneNumber = '+233' + phoneNumber.replace(/^\+?233/, '');
        }
      }

      // Map mobileMoneyDetails to bankDetails for backend
      const updateData = {
        personalInfo: {
          ...settingsFormData.personalInfo,
          phone: phoneNumber
        },
        bankDetails: {
          bankName: settingsFormData.mobileMoneyDetails.provider,
          accountNumber: settingsFormData.mobileMoneyDetails.mobileNumber,
          accountName: settingsFormData.mobileMoneyDetails.accountName,
          accountType: 'savings'
        }
      };

      // Clean up empty strings
      if (updateData.personalInfo.idNumber === '') {
        delete updateData.personalInfo.idNumber;
      }
      if (updateData.personalInfo.dateOfBirth === '') {
        delete updateData.personalInfo.dateOfBirth;
      }
      if (updateData.personalInfo.nationality === '') {
        delete updateData.personalInfo.nationality;
      }
      if (updateData.personalInfo.address.street === '') {
        delete updateData.personalInfo.address.street;
      }
      if (updateData.personalInfo.address.city === '') {
        delete updateData.personalInfo.address.city;
      }

      const token = localStorage.getItem('affiliateToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';
      const response = await fetch(`${API_BASE_URL}/api/affiliates/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (response.ok) {
        setSettingsSuccess('Profile updated successfully!');
        setEditMode(false);
        // Refresh dashboard data
        await fetchDashboardData();
        setTimeout(() => {
          setSettingsSuccess('');
        }, 3000);
      } else {
        const errorMessage = data.message || 
          (data.errors && data.errors.length > 0 ? data.errors[0].msg : null) ||
          'Failed to update profile';
        setSettingsError(errorMessage);
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setSettingsError('Network error. Please check your connection and try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handlePayoutRequest = async () => {
    try {
      const token = localStorage.getItem('affiliateToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';
      const response = await fetch(`${API_BASE_URL}/api/affiliates/payouts/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(payoutAmount),
          method: 'mobile_money'
        })
      });

      if (response.ok) {
        setPayoutDialogOpen(false);
        setPayoutAmount('');
        fetchDashboardData(); // Refresh data
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to request payout' }));
        throw new Error(errorData.message || 'Failed to request payout');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Container className={classes.container}>
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className={classes.container}>
        <Paper style={{ padding: 16, backgroundColor: '#ffebee', color: '#c62828', marginTop: 16 }}>
          <Typography variant="body1">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container className={classes.container}>
        <Paper style={{ padding: 16, backgroundColor: '#e3f2fd', color: '#1565c0', marginTop: 16 }}>
          <Typography variant="body1">No dashboard data available</Typography>
        </Paper>
      </Container>
    );
  }

  const { affiliate, statistics, recentReferrals, recentCommissions } = dashboardData;

  return (
    <Container className={classes.container}>
      {/* Header */}
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            Affiliate Dashboard
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboardData}
              className={classes.actionButton}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Settings />}
              onClick={() => setSettingsDialogOpen(true)}
              className={classes.actionButton}
            >
              Settings
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExitToApp />}
              onClick={handleLogout}
              className={classes.actionButton}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {/* Referral Code Display */}
        <div className={classes.referralCode}>
          Your Referral Code: {affiliate.referralCode}
        </div>

        {/* Commission Info */}
        <div className={classes.commissionInfo}>
          <Typography variant="h6" gutterBottom>
            💰 Commission Structure
          </Typography>
          <Typography variant="body2">
            • Earn <strong>100 GHS</strong> per successful school referral
          </Typography>
          <Typography variant="body2">
            • Commission paid after school uses the system for <strong>at least one term</strong>
          </Typography>
          <Typography variant="body2">
            • Minimum payout threshold: <strong>GHS 700</strong>
          </Typography>
        </div>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <People fontSize="large" />
              <Typography variant="h4" component="h2">
                {statistics.totalReferrals}
              </Typography>
              <Typography variant="body2">
                Total Referrals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCardGreen}>
            <CardContent>
              <CheckCircle fontSize="large" />
              <Typography variant="h4" component="h2">
                {statistics.successfulReferrals}
              </Typography>
              <Typography variant="body2">
                Successful Referrals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCardOrange}>
            <CardContent>
              <AttachMoney fontSize="large" />
              <Typography variant="h4" component="h2">
                GHS {statistics.totalEarnings?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2">
                Total Earnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCardPurple}>
            <CardContent>
              <TrendingUp fontSize="large" />
              <Typography variant="h4" component="h2">
                {statistics.conversionRate?.toFixed(1) || '0'}%
              </Typography>
              <Typography variant="body2">
                Conversion Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Referral Link and QR Code */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          📤 Share Your Referral Link
        </Typography>
        
        <div className={classes.referralLink}>
          <TextField
            fullWidth
            variant="outlined"
            value={affiliate.referralLink}
            InputProps={{
              readOnly: true,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={copyReferralLink}
            startIcon={<Share />}
          >
            Copy
          </Button>
        </div>

        {/* Social Media Share Buttons */}
        <Box mt={3} display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={shareToWhatsApp}
            style={{ backgroundColor: '#25D366', color: 'white' }}
            startIcon={<Share />}
          >
            Share on WhatsApp
          </Button>
          <Button
            variant="contained"
            onClick={shareToFacebook}
            style={{ backgroundColor: '#1877F2', color: 'white' }}
            startIcon={<Share />}
          >
            Share on Facebook
          </Button>
          <Button
            variant="contained"
            onClick={shareToTwitter}
            style={{ backgroundColor: '#1DA1F2', color: 'white' }}
            startIcon={<Share />}
          >
            Share on Twitter
          </Button>
        </Box>

        {qrCodeUrl && (
          <div className={classes.qrCode}>
            <Typography variant="subtitle1" gutterBottom>
              QR Code for Easy Sharing
            </Typography>
            <img src={qrCodeUrl} alt="Referral QR Code" style={{ maxWidth: 200 }} />
            <br />
            <Button
              variant="outlined"
              onClick={downloadQRCode}
              startIcon={<GetApp />}
              style={{ marginTop: 16 }}
            >
              Download QR Code
            </Button>
          </div>
        )}
      </Paper>

      {/* Financial Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              💳 Financial Summary
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <AttachMoney />
                </ListItemIcon>
                <ListItemText
                  primary="Total Earnings"
                  secondary={`GHS ${statistics.totalEarnings?.toFixed(2) || '0.00'}`}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Schedule />
                </ListItemIcon>
                <ListItemText
                  primary="Pending Commissions"
                  secondary={`GHS ${statistics.pendingCommissions?.toFixed(2) || '0.00'}`}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle />
                </ListItemIcon>
                <ListItemText
                  primary="Paid Commissions"
                  secondary={`GHS ${statistics.paidCommissions?.toFixed(2) || '0.00'}`}
                />
              </ListItem>
            </List>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => setPayoutDialogOpen(true)}
              disabled={statistics.pendingCommissions < 700}
            >
              Request Payout (Min: GHS 700)
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              📊 Performance Metrics
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <People />
                </ListItemIcon>
                <ListItemText
                  primary="Total Referrals"
                  secondary={statistics.totalReferrals}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle />
                </ListItemIcon>
                <ListItemText
                  primary="Successful Referrals"
                  secondary={statistics.successfulReferrals}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Schedule />
                </ListItemIcon>
                <ListItemText
                  primary="Pending Referrals"
                  secondary={statistics.pendingReferrals}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <TrendingUp />
                </ListItemIcon>
                <ListItemText
                  primary="Conversion Rate"
                  secondary={`${statistics.conversionRate?.toFixed(1) || '0'}%`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Referrals */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          🏫 Recent Referrals
        </Typography>
        
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>School Name</TableCell>
                <TableCell>School ID</TableCell>
                <TableCell>Referred Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Term Usage</TableCell>
                <TableCell>Commission</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentReferrals?.map((referral, index) => (
                <TableRow key={index}>
                  <TableCell>{referral.schoolName}</TableCell>
                  <TableCell>{referral.schoolID}</TableCell>
                  <TableCell>
                    {new Date(referral.referredAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={referral.status}
                      color={
                        referral.status === 'converted' ? 'primary' :
                        referral.status === 'pending' ? 'default' : 'secondary'
                      }
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{referral.termUsage || 'Pending'}</TableCell>
                  <TableCell>GHS {referral.commission?.toFixed(2) || '0.00'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Commissions */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          💰 Recent Commissions
        </Typography>
        
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>School Name</TableCell>
                <TableCell>Commission Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Term Completed</TableCell>
                <TableCell>Paid Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentCommissions?.map((commission, index) => (
                <TableRow key={index}>
                  <TableCell>{commission.schoolName || 'N/A'}</TableCell>
                  <TableCell>GHS {commission.commissionAmount?.toFixed(2) || commission.totalCommission?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    <Chip
                      label={commission.status}
                      color={
                        commission.status === 'paid' ? 'primary' :
                        commission.status === 'pending' ? 'default' : 'secondary'
                      }
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{commission.termCompleted || 'Pending'}</TableCell>
                  <TableCell>
                    {commission.paidAt ? new Date(commission.paidAt).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payout Request Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)}>
        <DialogTitle>Request Payout</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Payout Amount (GHS)"
            type="number"
            fullWidth
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            helperText={`Available: GHS ${statistics.pendingCommissions?.toFixed(2) || '0.00'}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePayoutRequest} color="primary">
            Request Payout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => {
          setSettingsDialogOpen(false);
          setEditMode(false);
        }} 
        maxWidth="md" 
        fullWidth
        TransitionProps={{
          onEntered: () => {
            // Ensure data is loaded when dialog fully opens
            console.log('Dialog transition completed, checking for data...');
            if (dashboardData?.affiliate) {
              console.log('Loading data on dialog entered');
              const personalInfo = dashboardData.affiliate.personalInfo || {};
              const bankDetails = dashboardData.affiliate.bankDetails || {};
              
              setSettingsFormData({
                personalInfo: {
                  fullName: personalInfo.fullName || '',
                  email: personalInfo.email || '',
                  phone: personalInfo.phone || '',
                  idNumber: personalInfo.idNumber || '',
                  address: {
                    street: personalInfo.address?.street || '',
                    city: personalInfo.address?.city || '',
                    state: personalInfo.address?.state || '',
                    zipCode: personalInfo.address?.zipCode || '',
                    country: personalInfo.address?.country || 'Ghana'
                  },
                  gender: personalInfo.gender || '',
                  dateOfBirth: personalInfo.dateOfBirth ? (personalInfo.dateOfBirth.split('T')[0] || '') : '',
                  nationality: personalInfo.nationality || ''
                },
                mobileMoneyDetails: {
                  provider: bankDetails.bankName || '',
                  mobileNumber: bankDetails.accountNumber || '',
                  accountName: bankDetails.accountName || ''
                }
              });
            } else {
              console.log('No data yet, will load when available');
            }
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Account Settings</Typography>
            {!editMode && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  loadProfileDataIntoForm();
                  setEditMode(true);
                }}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {settingsError && (
            <Paper style={{ padding: 12, backgroundColor: '#ffebee', color: '#c62828', marginBottom: 16 }}>
              <Typography variant="body2">{settingsError}</Typography>
            </Paper>
          )}
          {settingsSuccess && (
            <Paper style={{ padding: 12, backgroundColor: '#e8f5e9', color: '#2e7d32', marginBottom: 16 }}>
              <Typography variant="body2">{settingsSuccess}</Typography>
            </Paper>
          )}

          <Tabs value={settingsTab} onChange={(e, newValue) => setSettingsTab(newValue)}>
            <Tab label="Personal Information" />
            <Tab label="Mobile Money Details" />
            <Tab label="Account Info" />
          </Tabs>

          {/* Personal Information Tab */}
          {settingsTab === 0 && (
            <Box mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.fullName"
                    label="Full Name"
                    value={settingsFormData.personalInfo.fullName}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.email"
                    label="Email Address"
                    type="email"
                    value={settingsFormData.personalInfo.email}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.phone"
                    label="Phone Number"
                    value={settingsFormData.personalInfo.phone}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    placeholder="+233XXXXXXXXX"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.idNumber"
                    label="ID Number"
                    value={settingsFormData.personalInfo.idNumber}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" disabled={!editMode}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="personalInfo.gender"
                      value={settingsFormData.personalInfo.gender}
                      onChange={handleSettingsChange}
                      label="Gender"
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.dateOfBirth"
                    label="Date of Birth"
                    type="date"
                    value={settingsFormData.personalInfo.dateOfBirth}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.nationality"
                    label="Nationality"
                    value={settingsFormData.personalInfo.nationality}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.address.street"
                    label="Street Address"
                    value={settingsFormData.personalInfo.address.street}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="personalInfo.address.city"
                    label="City"
                    value={settingsFormData.personalInfo.address.city}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Mobile Money Details Tab */}
          {settingsTab === 1 && (
            <Box mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" disabled={!editMode} required>
                    <InputLabel>Mobile Money Provider</InputLabel>
                    <Select
                      name="mobileMoneyDetails.provider"
                      value={settingsFormData.mobileMoneyDetails.provider}
                      onChange={handleSettingsChange}
                      label="Mobile Money Provider"
                    >
                      <MenuItem value="MTN">MTN Mobile Money</MenuItem>
                      <MenuItem value="Vodafone">Vodafone Cash</MenuItem>
                      <MenuItem value="AirtelTigo">AirtelTigo Money</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="mobileMoneyDetails.mobileNumber"
                    label="Mobile Money Number"
                    value={settingsFormData.mobileMoneyDetails.mobileNumber}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    placeholder="0244XXXXXX or 050XXXXXXX"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="mobileMoneyDetails.accountName"
                    label="Account Name (Name on Mobile Money Account)"
                    value={settingsFormData.mobileMoneyDetails.accountName}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant="outlined"
                    disabled={!editMode}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Account Info Tab */}
          {settingsTab === 2 && (
            <Box mt={3}>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Referral Code"
                    secondary={dashboardData?.affiliate?.referralCode || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Account Status"
                    secondary={
                      <Chip
                        label={dashboardData?.affiliate?.status || 'pending'}
                        color={
                          dashboardData?.affiliate?.status === 'active' ? 'primary' :
                          dashboardData?.affiliate?.status === 'pending' ? 'default' : 'secondary'
                        }
                        size="small"
                      />
                    }
                  />
                </ListItem>
              </List>
              <Divider style={{ margin: '16px 0' }} />
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                startIcon={<ExitToApp />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={() => {
                setEditMode(false);
                setSettingsError('');
                setSettingsSuccess('');
                // Reload form data from dashboard
                loadProfileDataIntoForm();
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProfile} 
                color="primary" 
                variant="contained"
                disabled={settingsLoading}
              >
                {settingsLoading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setSettingsDialogOpen(false)} color="primary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AffiliateDashboard;
